// 1. React・ライブラリ
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Link } from '@mui/material'
import { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'

// 2. CSS・スタイル
import 'react-datepicker/dist/react-datepicker.css'

// 3. 共通ユーティリティ・サービス
import { errorLog } from '../../../common/logger/Logger'
import { httpPost } from '../../../services/api/AmplifyApi'
import { getDataCreateTask } from '../../../common/utils/DynamoDbUtils'

// 4. 定数
import { viewId, titleName, restApiKey, masterClassification } from '../../../_constants/Code'
import { PathInfoList } from '../../../_constants/PathInfoList'

// 5. カスタムHooks・モーダル
import { useModal } from '../../../_components/Modal'

// 6. 自モジュール内のサービス・ユーティリティ
import {
  fetchCodeMaster,
  fetchFiscalYearList,
  fetchLegalDisclosure,
  fetchMasterData,
  fetchCompanyConfig,
  getDataList
} from './extra/services'
import { checkConfirmedStatus, calculateCurrentYear } from './extra/helper'
import { mergeEmploymentTypes } from './extra/dateUtils'

// 7. 他モジュールからのユーティリティ・サービス
import { getFiscalYear } from '../employeeCountInfo/extra/fiscalYearUtils'
import { fetchEmploymentTypesInfo } from '../employeeCountInfo/extra/services'
import { parseEmploymentTypeCsv } from '../employeeCountInfo/extra/dataParsers'

// 8. コンポーネント
import RelatedDashboard from '../parts/RelatedDashboard'
import SecondedEmployeeHandlingSettings, {
  useIndicatorConditionSelection
} from './components/SecondedEmployeeHandlingSettings'
import EmploymentCategorySettings, { useGenderTypeSelectionByFiscalYear } from './components/EmploymentCategorySettings'
import { CopyButton } from '../../common/CustomButton'
import { ChrofyCustomYearSelect } from '../../common/ChrofyCustomSelect'

// 9. その他コンポーネント
import Forbidden from '../../../_components/Forbidden'
import LoadingMessage from '../../../_components/LoadingMessage'
import PageLoading from '../../../_components/PageLoading'

// 日本語ロケールを登録
registerLocale('ja', ja)

/**
 * 決算年月文字列から対象期間の終了日・開始日を算出する。
 *
 * @param {string} fiscalYearMonthStr - 決算年月（例: "2024/03"）
 * @returns {[Date | null, Date | null]} [終了日, 開始日]。決算年月が無効なら [null, null]
 */
const deriveFiscalDates = (fiscalYearMonthStr) => {
  const fiscalYearEndDate = fiscalYearMonthStr ? new Date(fiscalYearMonthStr) : null
  if (!fiscalYearEndDate) return [null, null]
  const oneYearAgo = new Date(fiscalYearEndDate)
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
  const fiscalYearStartDate = new Date(oneYearAgo)
  fiscalYearStartDate.setDate(fiscalYearStartDate.getDate() + 1)
  return [fiscalYearEndDate, fiscalYearStartDate]
}

/** StatutoryDisclosureコンポーネント */
const StatutoryDisclosureContent = (props) => {
  // 各種状態管理（ステート）
  const [isLoaded, setIsLoaded] = useState(false) // データの読み込み完了フラグ
  const [isLoading, setIsLoading] = useState(true) // データの読み込み完了フラグ
  const [master, setMaster] = useState([]) // マスター情報
  const [fiscalYearList, setFiscalYearList] = useState([]) // 年度リスト

  const [serverError, setServerError] = useState({}) // サーバーエラー情報
  const [inputError, setInputError] = useState({}) // 入力エラー情報
  // 選択中の年度。初期値をマウント時に同期計算し、年度別設定の取得を初期表示の他fetchと並行して開始する
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(() => calculateCurrentYear(props))
  const [isConfirmed, setIsConfirmed] = useState(true) // データ確定状態フラグ
  const [confirmErrorMessage, setConfirmErrorMessage] = useState('') // 確定時のエラーメッセージ
  const [dataCreateTask, setDataCreateTask] = useState([])
  const [hasPreviousYearData, setHasPreviousYearData] = useState(false)

  const [indicatorInfo, setIndicatorInfo] = useState(null)
  const [employmentType, setEmploymentType] = useState(null)
  const [genderStatisticalClassificationInfo, setGenderStatisticalClassificationInfo] = useState(null)
  const [companyConfig, setCompanyConfig] = useState({})
  const [legalDisclosureData, setLegalDisclosureData] = useState(null)

  const { selectedCondition, selectedConditionsByYear, setSelectedConditionsByYear, handleChangeCondition } =
    useIndicatorConditionSelection(selectedFiscalYear)
  const { selectedGenderTypes, selectedGenderTypesByYear, setSelectedGenderTypesByYear, handleChangeGenderType } =
    useGenderTypeSelectionByFiscalYear(selectedFiscalYear)

  // 選択年度が変化したタイミングで処理を実行
  useEffect(() => {
    if (!selectedFiscalYear || fiscalYearList.length === 0) {
      setHasPreviousYearData(false)
      return
    }

    // fiscalYearList から年度文字列を取得し、整数に変換する
    const yearsIntList = fiscalYearList.map((yearStr) => parseInt(yearStr.replace('年度', ''), 10))
    const minYear = Math.min(...yearsIntList)
    // 選択中の年度を整数に変換
    const selectedYearInt = parseInt(selectedFiscalYear.replace('年度', ''), 10)
    // 選択した年度が最小年度の場合のみ、コピー用ボタンを無効化
    setHasPreviousYearData(selectedYearInt !== minYear)
    // 選択された年度の確定状況をチェック
    checkConfirmedStatus(selectedYearInt, props.accPeriod, dataCreateTask).then((confirmed) => {
      setIsConfirmed(confirmed)
      const message = confirmed ? '対象の年度は確定済みのため、編集できません。' : ''
      setConfirmErrorMessage(message) // 確定済みの場合、編集不可のメッセージを設定
    })
  }, [selectedFiscalYear, fiscalYearList, dataCreateTask])

  // 年度ごとにcompanyConfigを取得
  const selectedConfig = companyConfig?.[selectedFiscalYear] ?? null

  // 年度ごとに決算年月を取得
  const fiscalYearMonthStr = selectedFiscalYear ? getDataList(props, selectedFiscalYear)?.['決算年月'] || '' : ''

  // 決算年月から計算した期間の開始日・終了日を取得
  const [fiscalYearEndDate, fiscalYearStartDate] = useMemo(
    () => deriveFiscalDates(fiscalYearMonthStr),
    [fiscalYearMonthStr]
  )

  // 年度選択リスト定義
  const options = useMemo(() => {
    return fiscalYearList.map((fiscalYear) => ({
      label: getFiscalYear(fiscalYear, props.accPeriod),
      value: fiscalYear
    }))
  }, [fiscalYearList, props.accPeriod])

  // 年度切り替え
  const handleSelectChange = useCallback(
    (fiscalYear) => {
      setSelectedFiscalYear(fiscalYear)
    },
    [setSelectedFiscalYear]
  )

  // 初期処理
  useEffect(() => {
    document.title = props.pageTitle
    window.scrollTo(0, 0)
    init()
  }, [])

  // 初期処理完了後にスクロールバー設定
  useEffect(() => {
    if (isLoaded) setScrollBar()
  }, [isLoaded])

  /**
   * 初期データの取得・状態設定を行う非同期関数。
   *
   * - 指標情報、雇用区分情報、ジェンダー統計区分情報、会社設定情報をAPIから取得。
   * - マスター情報、タスク情報を並行して取得。
   * - 年度リストを取得し、初期選択年度を設定。
   *
   * @async
   * @returns {Promise<void>}
   */
  const init = async () => {
    try {
      // 指標一覧・雇用区分・ジェンダー統計区分・法人設定は互いに独立のため並列取得する
      const indicator = '指標'
      const genderStatisticalClassification = 'ジェンダー統計区分'
      const securitiesReport = 'SecuritiesReport'

      const [indicatorInfo, employmentTypeData, genderStatisticalClassificationInfo, companyData] = await Promise.all([
        fetchCodeMaster(props.corporationNumber, indicator).catch(),
        fetchEmploymentTypesInfo(props.bucketName),
        fetchCodeMaster(props.corporationNumber, genderStatisticalClassification),
        fetchCompanyConfig(props.corporationNumber, securitiesReport)
      ])

      const employmentType = parseEmploymentTypeCsv(employmentTypeData)

      setIndicatorInfo(indicatorInfo)
      setEmploymentType(employmentType)
      setGenderStatisticalClassificationInfo(genderStatisticalClassificationInfo)
      setCompanyConfig(companyData)

      // 年度取得
      const currentYear = calculateCurrentYear(props)
      // 選択年度 or 現在進行年度
      const _selectedYear = selectedFiscalYear || currentYear

      // マスター情報取得
      const _masterData = fetchMasterData(props)
      // データ作成タスク取得
      const _dataCreateTask = getDataCreateTask(props.corporationNumber, props.corporationNumber)

      // 処理完了まで待機
      const [masterData, dataCreateTask] = await Promise.all([_masterData, _dataCreateTask])

      // 年度リスト作成
      const yearList = await fetchFiscalYearList(currentYear, props)

      setMaster(masterData)
      setDataCreateTask(dataCreateTask)

      // 年度情報設定
      if (yearList && yearList.length > 0) {
        setFiscalYearList(yearList)
        // 初期値と同値なら再設定してもuseEffectは再発火しない（初期並行fetchを重複させない）
        setSelectedFiscalYear(_selectedYear)
      } else {
        // 年度リストが無い場合は選択年度を未選択に戻す（初期値プリセット前の従来挙動を維持）
        setSelectedFiscalYear('')
      }
    } catch (error) {
      errorLog('E000001', props.userId, viewId.statutoryDisclosure, 'init', error)
      setServerError({ errorMessage: 'データ取得エラーが発生しました' })
    } finally {
      setIsLoaded(true)
    }
  }

  // 選択年度変更時に年度ごとの画面情報取得
  useEffect(() => {
    if (!selectedFiscalYear) return

    // エラー状態をクリア
    setInputError({})

    setIsLoading(true)
    loadFiscalYearSettings()
  }, [selectedFiscalYear])

  /**
   * 選択中の年度に応じた画面項目（設定）を取得し、状態にセットする非同期関数。
   *
   * - すでに年度ごとの設定が存在する場合はAPI呼び出しをせずに終了する。
   * - 指定された年度の法定開示データをAPIから取得し、 出向者の取り扱い設定および雇用区分に関する設定をそれぞれ状態にセットする。
   * - 画面の読み込み状態(isLoading)を管理する。
   *
   * @async
   * @returns {Promise<void>} 処理完了を示すPromise
   */
  const loadFiscalYearSettings = async () => {
    if (selectedConditionsByYear[selectedFiscalYear]) {
      setIsLoading(false)
      return
    }
    const legalDisclosureData = await fetchLegalDisclosureData()
    if (!selectedFiscalYear || !legalDisclosureData) return

    // 出向者の取り扱い設定を取得
    const secondmentObj = Object.fromEntries(
      legalDisclosureData.legal_secondment.map((e) => [e.index_code, e.secondment])
    )
    setSelectedConditionsByYear((prev) => {
      if (prev[selectedFiscalYear]) {
        return prev
      }
      return {
        ...prev,
        [selectedFiscalYear]: secondmentObj
      }
    })

    // 雇用区分に関する設定を取得
    const employmentTypeObj = Object.fromEntries(
      legalDisclosureData.legal_employment_type.map((e) => [e.employment_type, e.gender_statistics_code])
    )
    setSelectedGenderTypesByYear((prev) => {
      if (prev[selectedFiscalYear]) {
        return prev
      }
      return {
        ...prev,
        [selectedFiscalYear]: employmentTypeObj
      }
    })
  }

  // 法定開示レポート設定画面取得
  const fetchLegalDisclosureData = async () => {
    // fiscalYearStartDate と fiscalYearEndDate が有効な日付かチェック
    if (
      !fiscalYearStartDate ||
      !fiscalYearEndDate ||
      isNaN(fiscalYearStartDate.getTime()) ||
      isNaN(fiscalYearEndDate.getTime())
    ) {
      // 日付が無効ならAPI呼び出しをスキップ
      return
    }

    const data = await fetchLegalDisclosure(props, fiscalYearStartDate, fiscalYearEndDate)
    setLegalDisclosureData(data)
    setIsLoading(false)
    return data
  }

  // 選択年度に含まれる雇用区分のみ抽出
  const filteredEmploymentType = useMemo(() => {
    if (!employmentType) return []
    return employmentType.filter((e) => {
      const validStart = e.validStartDate ? new Date(e.validStartDate) : null
      const validEnd = e.validEndDate ? new Date(e.validEndDate) : null

      // 有効期間が選択年度期間と重複していない場合は除外
      const noOverlap = (validEnd && validEnd < fiscalYearStartDate) || (validStart && validStart > fiscalYearEndDate)

      return !noOverlap
    })
  }, [employmentType, fiscalYearMonthStr])

  // 雇用区分の名称が重複している場合はマージ
  const uniqueEmploymentType = mergeEmploymentTypes(filteredEmploymentType)

  /** スクロールバー設定 */
  const setScrollBar = () => {
    // ＜ボタンにdisabledをつける
    document.querySelectorAll('.setting-table__btn-scroll-left').forEach((v) => v.setAttribute('disabled', ''))
    // スクロールイベント登録
    document.querySelectorAll('.setting-table__scroll').forEach((element) => {
      if (element.classList.contains('is-scroll')) return
      element.addEventListener('scroll', (event) => {
        // 要素の取得
        const $parent = event.target.closest('.setting-table')
        const $tableWrapper = $parent.querySelector('.setting-table__scroll')
        const $btnScrollLeft = $parent.querySelector('.setting-table__btn-scroll-left')
        const $btnScrollRight = $parent.querySelector('.setting-table__btn-scroll-right')
        const isScrollEnd = $tableWrapper.scrollWidth - ($tableWrapper.scrollLeft + $tableWrapper.clientWidth) < 1

        $btnScrollLeft.toggleAttribute('disabled', $tableWrapper.scrollLeft === 0)
        $btnScrollRight.toggleAttribute('disabled', isScrollEnd)
      })
      element.classList.add('is-scroll')
    })
  }

  /**
   * 選択中年度の前年の設定をコピーして反映する処理。 前年の設定が存在しない場合は入力状態を初期化する。
   *
   * @async
   * @returns {Promise<void>}
   */
  const handleCopyPreviousSettings = async () => {
    if (!selectedFiscalYear) return

    // 前年度の年度文字列作成（"2023年度" など）
    const prevYearNum = parseInt(selectedFiscalYear.replace('年度', ''), 10) - 1
    const prevYear = `${prevYearNum}年度`

    const prevConditions = selectedConditionsByYear?.[prevYear]
    const prevGenders = selectedGenderTypesByYear?.[prevYear]

    // 前年度の開始日、終了日を算出
    const prevFiscalYearStartDate = new Date(fiscalYearStartDate)
    const prevFiscalYearEndDate = new Date(fiscalYearEndDate)

    prevFiscalYearStartDate.setFullYear(prevFiscalYearStartDate.getFullYear() - 1)
    prevFiscalYearEndDate.setFullYear(prevFiscalYearEndDate.getFullYear() - 1)

    // 前年データ未作成の場合、取得後に設定
    if (!prevConditions || !prevGenders) {
      // 前年度データが未作成の場合にデータ取得する
      const prevData = await fetchLegalDisclosure(props, prevFiscalYearStartDate, prevFiscalYearEndDate)

      // 前年度の出向者の取り扱い設定を取得
      const prevSecondmentObj = Object.fromEntries(prevData.legal_secondment.map((e) => [e.index_code, e.secondment]))
      setSelectedConditionsByYear((prev) => {
        if (prev[prevYear]) {
          return prev
        }
        return {
          ...prev,
          [prevYear]: prevSecondmentObj
        }
      })

      // 前年度の雇用区分に関する設定を取得
      const prevEmploymentTypeObj = Object.fromEntries(
        prevData.legal_employment_type.map((e) => [e.employment_type, e.gender_statistics_code])
      )
      setSelectedGenderTypesByYear((prev) => {
        if (prev[prevYear]) {
          return prev
        }
        return {
          ...prev,
          [prevYear]: prevEmploymentTypeObj
        }
      })

      setSelectedConditionsByYear((prev) => ({
        ...prev,
        [selectedFiscalYear]: prevSecondmentObj
      }))

      setSelectedGenderTypesByYear((prev) => ({
        ...prev,
        [selectedFiscalYear]: prevEmploymentTypeObj
      }))

      return
    }

    // 現在の年度に前年の設定をセット
    setSelectedConditionsByYear((prev) => ({
      ...prev,
      [selectedFiscalYear]: prevConditions
    }))

    setSelectedGenderTypesByYear((prev) => ({
      ...prev,
      [selectedFiscalYear]: prevGenders
    }))
  }

  /**
   * 出向者の取り扱い設定および雇用区分の入力値を検証し、 エラーがあれば入力エラー状態をセットする。
   *
   * @param {Record<string, string>} selectedCondition - 指標ごとの選択条件マップ
   * @param {Record<string, string>} selectedGenderTypes - 雇用区分ごとの選択ジェンダーマップ
   * @returns {boolean} エラーがあればtrue、問題なければfalse
   */
  const validateInput = (selectedCondition = {}, selectedGenderTypes = {}) => {
    const errorMap = {
      indicatorInfo: {},
      gender: {}
    }

    // 指標エラー取得
    for (const indicatorType of indicatorInfo.Config) {
      const value = selectedCondition[indicatorType.Code]
      if (!value) {
        errorMap.indicatorInfo[indicatorType.Code] = true
      }
    }

    // 雇用区分エラー取得
    for (const empType of uniqueEmploymentType) {
      const value = selectedGenderTypes[empType.employmentType]
      if (!value) {
        errorMap.gender[empType.employmentType] = true
      }
    }

    if (Object.keys(errorMap.indicatorInfo).length > 0 || Object.keys(errorMap.gender).length > 0) {
      setInputError({ errorMessage: '入力内容に誤りがあります。', ...errorMap })
      return true
    } else {
      setInputError({})
      return false
    }
  }

  /**
   * 入力チェック後、マスター設定値をAPIに送信し、更新処理を実行する。
   *
   * @param {Record<string, string>} selectedCondition - 出向者の取り扱い設定
   * @param {Record<string, string>} selectedGenderTypes - 雇用区分に関する設定
   * @returns {Promise<void>}
   */
  const handleUpdateStatutoryDisclosure = async (selectedCondition, selectedGenderTypes) => {
    if (validateInput(selectedCondition, selectedGenderTypes)) {
      window.scrollTo(0, 0)
      return
    }

    try {
      const title = 'マスター設定値登録の確認'
      const description = (
        <>
          <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
          <div className="pt-5 text-center">
            ※ダッシュボードへの反映には5分～10分程度かかります
            <br />
            マスター管理画面のステータスから反映状況を確認してください
          </div>
        </>
      )

      const buttons = [
        {
          name: '実行',
          className: 'footer-btn button_base',
          click: async () => {
            //click→serviceに移動
            try {
              setServerError({})

              const init = {
                body: {
                  corporation_name: props?.corporationName,
                  corporation_number: props.corporationNumber,
                  user_name: props.userName,
                  user_sub: props.userId,
                  fiscal_year: selectedFiscalYear, // 選択年度
                  period: fiscalYearMonthStr, // 決算年月

                  legal_secondment: Object.entries(selectedCondition).map(([k, v]) => ({
                    index_code: k,
                    secondment: v
                  })),

                  legal_employment_type: Object.entries(selectedGenderTypes).map(([k, v]) => ({
                    employment_type: k,
                    gender_statistics_code: v
                  }))
                },
                headers: {}
              }
              await httpPost(
                restApiKey.CHROFY_REST_API,
                PathInfoList.Resource.updateLegalDisclosure(props.corporationNumber),
                init
              ).then((res) => {
                if (res.message.indexOf('Success') !== -1) {
                  props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
                } else {
                  setServerError({ errorMessage: '更新処理でエラーが発生しました。' })
                  window.scrollTo(0, 0)
                }
              })
            } catch (error) {
              errorLog('E000001', props.userId, viewId.statutoryDisclosure, 'updateLegalDisclosure', error)
            }
          }
        }
      ]
      props.showModal({ title, description, button: buttons, isWide: true })
    } catch (error) {
      errorLog('E000001', props.userId, viewId.statutoryDisclosure, 'updateLegalDisclosure', error)
    }
  }

  /** 描画処理 */
  if (!isLoaded) {
    return <PageLoading />
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>

            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification?.[master.Classification]?.name || ''}
              </h1>
            </header>

            {/*  コンテンツボディ */}
            <div className="page-body">
              <div>
                <span className="text-red-600">{inputError.errorMessage}</span>
                <span className="text-red-600">{serverError.errorMessage}</span>
              </div>

              {/*  ===== 平均年収の設定 ===== */}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  法定開示レポート設定
                </h2>

                <p className="mt-[20px]">
                  法定開示指標の計算に必要な設定を行います。ここで設定された内容は「10
                  法定開示レポート」のダッシュボードの表示結果に反映されます。
                </p>

                <div
                  className="box-graph__target-fiscal-year flex items-center"
                  style={{ marginTop: '20px', justifyContent: 'space-between' }}
                >
                  {/* 左側：「対象」とドロップダウン */}
                  <div className="flex items-center">
                    <div className="text-h5">対象：</div>
                    <ChrofyCustomYearSelect
                      id="chrofy-custom-select"
                      value={selectedFiscalYear}
                      onChange={handleSelectChange}
                      options={options}
                      disabled={false}
                      hasNoSelect={false}
                    />
                  </div>

                  {/* 右側：「前期設定コピー」ボタン */}
                  <div className="btn-group pl-10 border-l border-blue2-lighten-20">
                    <CopyButton onClick={handleCopyPreviousSettings} disabled={!hasPreviousYearData} />
                  </div>
                </div>
                <div
                  role="alert"
                  aria-live="assertive"
                  className={`text-red-600 min-h-[30px] ${isConfirmed ? 'visible my-2' : 'invisible'}`}
                  style={{ lineHeight: 1.2 }}
                >
                  {confirmErrorMessage}
                </div>

                <div className="border-t border-blue1 mb-4 -mx-16"></div>

                {isLoading && <LoadingMessage message="データ読み込み中..." minHeightClassName="min-h-[200px]" />}

                <div className={`${isLoading && 'hidden'}`} style={{ marginBottom: '2rem' }}>
                  {/* 出向者の取り扱い設定 */}
                  <SecondedEmployeeHandlingSettings
                    indicatorInfo={indicatorInfo}
                    selectedCondition={selectedCondition}
                    handleChangeCondition={handleChangeCondition}
                    inputError={inputError}
                  />
                </div>

                <div className={`${isLoading && 'hidden'}`} style={{ marginBottom: '2rem' }}>
                  {/* 雇用区分に関する設定 */}
                  <EmploymentCategorySettings
                    employmentType={uniqueEmploymentType}
                    genderStatisticalClassificationInfo={genderStatisticalClassificationInfo}
                    selectedConfig={selectedConfig}
                    selectedGenderTypes={selectedGenderTypes}
                    handleChangeGenderType={handleChangeGenderType}
                    inputError={inputError}
                  />
                </div>

                {/* エラーメッセージ表示 */}
                <div className="content-footer mt-16 mb-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    <button
                      className="btn footer-btn button_base"
                      onClick={() => handleUpdateStatutoryDisclosure(selectedCondition, selectedGenderTypes)}
                      disabled={isConfirmed}
                      aria-disabled={isConfirmed}
                    >
                      <div>登録</div>
                    </button>
                  </div>
                </div>
              </div>
              {/*  ===== 関連ダッシュボード ===== */}
              <RelatedDashboard dashboards={master.RelatedDashboard} authority={props.authority} mode={props.mode} />
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

/** StatutoryDisclosureコンポーネント ユーザーの権限に基づいて表示制御を行う */
const StatutoryDisclosure = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()

  // 権限がない場合はForbiddenコンポーネントを表示
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Dashboard.StatutoryDisclosure_Set
  ) {
    return <Forbidden />
  }

  return (
    <StatutoryDisclosureContent
      pageTitle={titleName.statutoryDisclosure + titleName.common}
      corporationNumber={state.company.corporationNumber}
      corporationName={state.company.name}
      bucketName={'chrofy-' + process.env.REACT_APP_ENV + '-' + state.company.corporationNumber}
      accPeriod={state.company.accPeriod}
      userId={state.user.sub}
      userName={state.user.userName}
      authority={state.authority}
      mode={state.analysis.mode}
      navigate={navigate}
      showModal={showModal}
    />
  )
}

export default StatutoryDisclosure
